import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { Grid, Typography } from "@mui/material";
import { CheckOutlined, CodeOutlined, EmailOutlined, SmsOutlined } from "@mui/icons-material";
import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import SendShipmentEmailDialog, {
  SendShipmentEmailDialogHandles,
} from "../../../../../../components/Dialogs/SendShipmentEmailDialog";
import Modal from "../../../../../../components/Generic/Modal";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import useActionShipment from "../../../../../../hooks/useActionShipment";
import useStyles, { CommunicationOption } from "./styles";

export interface IShipmentCommunicationModal {
  close: () => void;
  open: (shipmentIds: number[]) => void;
}

const ShipmentCommunicationModal: ForwardRefRenderFunction<IShipmentCommunicationModal> = (
  _,
  ref
) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const { useExportShipment } = useActionShipment();
  const {
    error: exportError,
    isError: isExportError,
    isLoading: isExportLoading,
    isSuccess: isExportSuccess,
    mutate: exportShipment,
  } = useExportShipment();

  const sendShipmentEmailRef = useRef<SendShipmentEmailDialogHandles>(null);

  const [open, setOpen] = useState<boolean>(false);
  const [optionIndex, setOptionIndex] = useState<number>(1);
  const [shipmentIds, setShipmentIds] = useState<number[]>([]);

  const closeModal = useCallback(() => setOpen(false), []);

  const openModal = useCallback((shipmentIds: number[]) => {
    setShipmentIds(shipmentIds);
    setOpen(true);
  }, []);

  useImperativeHandle(ref, () => ({ close: closeModal, open: openModal }));

  useEffect(() => closeModal(), [isExportSuccess]);
  useEffect(() => {
    if (isExportError) {
      console.error(exportError);
      NotificationToast.error(translate("anErrorOccurredTryAgain"));
      closeModal();
    }
  }, [isExportError]);

  const sendEmail = () => {
    sendShipmentEmailRef.current?.open(shipmentIds);
    closeModal();
  };

  const sendJSONFile = () => {
    shipmentIds.forEach((id) => exportShipment(id));
  };

  const sendTrackingViaSMSOrEmail = () => {
    sendShipmentEmailRef.current?.open(shipmentIds);
    closeModal();
  };

  const options = [
    {
      handleChoose: sendTrackingViaSMSOrEmail,
      icon: <SmsOutlined />,
      mainText: translate("sendTrackingViaSMSOrEmail"),
      subText: translate("sendShipmentTrackingViaSMSOrEmailToTheCustomerOrSomeoneElse"),
      disabled: true,
    },
    {
      handleChoose: sendEmail,
      icon: <EmailOutlined />,
      mainText: translate("sendEmail"),
      subText: translate("writeAnEmailOrUseATemplate"),
    },
    {
      handleChoose: sendJSONFile,
      icon: <CodeOutlined />,
      mainText: translate("sendJSONFile"),
      subText: translate("sendOnlyTheJSONFileToCustomer"),
      disabled: true,
    },
  ];

  const handleChoose = () => {
    options[optionIndex].handleChoose();
  };

  return (
    <>
      <Modal
        actionButtonProps={{
          loading: isExportLoading,
          onCancel: closeModal,
          onSave: handleChoose,
          textSave: translate("share"),
        }}
        className={classes.modal}
        handleClose={closeModal}
        open={open}
        showActionButtons
        title={translate("whatDoYouWantToSend")}
      >
        <Stack alignItems="stretch" className={classes.stack} direction="column" gap={"12px"}>
          {options.map((option, index) => {
            const selected = index === optionIndex;

            return (
              <CommunicationOption
                key={`communication-option-${index}`}
                onClick={() => !option?.disabled && setOptionIndex(index)}
                variant="outlined"
                selected={selected}
                disabled={option.disabled}
              >
                <Grid container spacing={2}>
                  <Grid item>{option.icon}</Grid>
                  <Grid item xs>
                    <Stack alignItems="stretch" direction="column" gap={"4px"}>
                      <Typography className="mainText">{option.mainText}</Typography>
                      <Typography className="subText">{option.subText}</Typography>
                    </Stack>
                  </Grid>
                  {selected && (
                    <Grid item>
                      <CheckOutlined style={{ height: "100%" }} />
                    </Grid>
                  )}
                </Grid>
              </CommunicationOption>
            );
          })}
        </Stack>
      </Modal>

      <SendShipmentEmailDialog ref={sendShipmentEmailRef} />
    </>
  );
};

export default forwardRef(ShipmentCommunicationModal);
